import { useState } from 'react'
import { Button, Stack, Text } from '@mantine/core'
import { IconDownload } from '@tabler/icons-react'

import type { RoboFrota } from '@/api/robo'
import { CHANGELOG } from '@/changelog'
import { capturarDiagnostico, erroEmTexto, montarRelatorio } from './diagnostico'
import { abrirLinhas, pedirPorta, temWebSerial } from './serial'

function baixar(nome: string, texto: string) {
  const url = URL.createObjectURL(new Blob([texto], { type: 'text/plain;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url
  a.download = nome
  a.click()
  // Liberar na hora pode cancelar o download antes de ele começar.
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

/**
 * Baixa um `.txt` com o que a placa no cabo diz e o cadastro dela no Console
 * (TASK-11865). Só lê a serial; nada vai ao backend.
 */
export function DiagnosticoPelaUsb({ robo }: { robo: RoboFrota }) {
  const [rodando, setRodando] = useState(false)
  const [resumo, setResumo] = useState<string | null>(null)

  if (!temWebSerial()) {
    return (
      <Text size="sm" c="dimmed">
        O diagnóstico pela USB funciona no Chrome ou no Edge, no computador.
      </Text>
    )
  }

  const gerar = async () => {
    // `pedirPorta` antes de qualquer outro await: o navegador só abre o
    // seletor de porta dentro do gesto do clique.
    let porta
    try {
      porta = await pedirPorta()
    } catch {
      return // fechou o seletor sem escolher
    }
    setRodando(true)
    setResumo(null)
    try {
      const captura = await capturarDiagnostico({
        abrir: (aoErro) => abrirLinhas(porta, 115200, aoErro),
      })
      const geradoEm = new Date()
      const texto = montarRelatorio(captura, {
        robo,
        versaoConsole: CHANGELOG[0].version,
        navegador: navigator.userAgent,
        portaUsb: porta.getInfo?.() ?? null,
        geradoEm,
      })
      const carimbo = geradoEm.toISOString().replace(/[:.]/g, '-')
      baixar(`diagnostico-robo-${robo.public_id}-${carimbo}.txt`, texto)
      const erros = captura.eventos.filter((e) => e.tipo === 'erro').length
      setResumo(captura.info
        ? `Diagnóstico baixado. A placa respondeu (${captura.info.versao}).`
        : erros
          ? 'Diagnóstico baixado. A conexão USB falhou durante a leitura; o erro está no arquivo.'
          : 'Diagnóstico baixado. A placa não respondeu no prazo; o que ela imprimiu está no arquivo.')
    } catch (erro) {
      setResumo(`Não foi possível gerar o diagnóstico: ${erroEmTexto(erro)}`)
    } finally {
      setRodando(false)
    }
  }

  return (
    <Stack gap={4} align="flex-start">
      <Text size="sm">
        Lê a placa no cabo por uns 20 segundos e baixa um arquivo com o que ela respondeu, o
        registro da conexão e o cadastro deste robô. Não grava nada no robô e não mostra
        credencial nem senha.
      </Text>
      <Button
        variant="default"
        leftSection={<IconDownload size={16} />}
        loading={rodando}
        onClick={gerar}
      >
        Baixar diagnóstico
      </Button>
      {resumo && <Text size="sm">{resumo}</Text>}
    </Stack>
  )
}
